/**
 * Global music-player context — Feb 2026.
 *
 * Holds the playlist state and the single shared HTMLAudioElement
 * that survives route navigation. Mounting the player UI at the App
 * root (outside <Routes>) keeps the audio element alive when the
 * user navigates between pages, which is the whole point of the
 * mini-player feature.
 *
 * The player auto-advances through the playlist on song end. The
 * user can play / pause / skip; we persist the "is playing" intent
 * in sessionStorage so a soft reload doesn't break flow, but we do
 * NOT autoplay across a full app boot (browsers block unprompted
 * autoplay).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MusicPlayerContext = createContext(null);

const SS_INDEX_KEY = "rm_player_idx";
const SS_VOLUME_KEY = "rm_player_volume";
// March 2026 — persist current playback position + play-intent across
// page reloads so the auto-cache-busting version-poll refresh (and
// any other reload — manual F5, mobile tab restore) does not cut the
// music off mid-song.
const SS_TIME_KEY = "rm_player_time";
const SS_PLAYING_KEY = "rm_player_playing";
// Anything older than this is treated as stale and discarded — we
// don't want the player to resume from the middle of a song the user
// listened to days ago when they reopen the site in a new session.
const SS_TIME_FRESHNESS_MS = 4 * 60 * 60 * 1000; // 4 hours
const SS_TIME_STAMP_KEY = "rm_player_time_ts";

export function MusicPlayerProvider({ children }) {
  const [songs, setSongs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [index, setIndex] = useState(() => {
    if (typeof sessionStorage === "undefined") return 0;
    const v = parseInt(sessionStorage.getItem(SS_INDEX_KEY) || "0", 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(() => {
    if (typeof sessionStorage === "undefined") return 0.7;
    const v = parseFloat(sessionStorage.getItem(SS_VOLUME_KEY) || "0.7");
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.7;
  });
  const audioRef = useRef(null);
  // Authoritative "user intent to play" signal that survives the
  // racy `pause` event fired by browsers immediately after `ended`.
  // Without this, the src-change effect would see `isPlaying=false`
  // when it runs for the auto-advance and skip autoplay — which is
  // exactly the bug where music stopped after the first song.
  const wantsToPlayRef = useRef(false);

  // Restore-on-boot state. We populate this once on mount inside an
  // effect (side-effects like sessionStorage.removeItem are not safe
  // inside the render path that useRef(initializer) sits on). The
  // src-change effect reads pendingRestoreRef and consumes it the
  // first time it runs.
  const pendingRestoreRef = useRef(null);
  // Once we've consumed the pending restore (applied the seek +
  // attempted autoplay), this flips to true so subsequent src changes
  // (the user clicking next / a song ending) start from t=0 like
  // normal. Without this, every next-track would also try to seek.
  const restoreConsumedRef = useRef(false);

  // Read the saved playback snapshot exactly once on mount. Stale
  // entries (older than SS_TIME_FRESHNESS_MS) are dropped so the
  // player doesn't pick up where the user left off days ago when
  // they reopen the site in a new session.
  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    try {
      const ts = parseInt(sessionStorage.getItem(SS_TIME_STAMP_KEY) || "0", 10);
      if (!Number.isFinite(ts) || Date.now() - ts > SS_TIME_FRESHNESS_MS) {
        sessionStorage.removeItem(SS_TIME_KEY);
        sessionStorage.removeItem(SS_PLAYING_KEY);
        sessionStorage.removeItem(SS_TIME_STAMP_KEY);
        return;
      }
      const time = parseFloat(sessionStorage.getItem(SS_TIME_KEY) || "0");
      const wasPlaying = sessionStorage.getItem(SS_PLAYING_KEY) === "1";
      pendingRestoreRef.current = {
        time: Number.isFinite(time) && time > 0 ? time : 0,
        wasPlaying,
      };
      // Pin the user's prior play intent so the src-change effect
      // auto-resumes once the audio element + playlist are ready.
      if (wasPlaying) wantsToPlayRef.current = true;
    } catch (_e) {
      /* no-op */
    }
  }, []);

  // Create the single shared Audio element on first client mount.
  // Using an effect (instead of inline lazy-init) keeps React's
  // rules-of-refs linter happy and ensures we only touch window
  // after hydration.
  useEffect(() => {
    if (audioRef.current == null && typeof window !== "undefined") {
      audioRef.current = new Audio();
      audioRef.current.preload = "metadata";
    }
  }, []);

  // Load the playlist on first mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/playlist`, { timeout: 8000 });
        if (cancelled) return;
        setSongs(res.data?.items || []);
      } catch (_e) {
        if (!cancelled) setSongs([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Wire the audio element's events once.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
    const onEnded = () => {
      // The user is "still listening" — pin the intent so the
      // src-change effect autoplays the next track even though the
      // `pause` event that follows `ended` will flip isPlaying to
      // false before React commits the new src.
      wantsToPlayRef.current = true;
      // Auto-advance to next song. We loop indefinitely until the
      // user explicitly stops — the playlist wraps from the last
      // track back to the first so there is no natural endpoint.
      setIndex((cur) => {
        const next = songs.length > 0 ? (cur + 1) % songs.length : 0;
        return next;
      });
    };
    const onPlay = () => {
      wantsToPlayRef.current = true;
      setIsPlaying(true);
    };
    const onPause = () => {
      // Only flip the "wants to play" intent when the pause came
      // from a real user action — NOT from the natural EOF pause
      // that follows the `ended` event. We detect EOF by checking
      // `audio.ended` (the browser sets this to true at EOF).
      if (!audio.ended) {
        wantsToPlayRef.current = false;
      }
      setIsPlaying(false);
    };
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [songs, volume]);

  // Whenever index OR the playlist content changes, point the audio
  // src at the new track. We auto-play the new src whenever the
  // user's "wants to play" intent is set — including the
  // immediately-after-ended case where React's `isPlaying` state has
  // already been flipped to false by the EOF `pause` event.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || songs.length === 0) return;
    const song = songs[index];
    if (!song) return;
    const newSrc = `${API}/playlist/${song.id}/stream`;
    if (audio.src === newSrc) return;
    // First src application after boot: if we have a pending restore
    // and the saved play-intent was "playing", treat that as
    // wantsToPlay so we auto-resume. Subsequent src changes (manual
    // next, song-end) ignore the restore.
    const pending = pendingRestoreRef.current;
    if (pending && !restoreConsumedRef.current && pending.wasPlaying) {
      wantsToPlayRef.current = true;
    }
    const wasPlaying = wantsToPlayRef.current || isPlaying;
    audio.src = newSrc;
    // If a saved currentTime is waiting, seek to it once the audio
    // element has enough metadata. Seeking before `loadedmetadata` is
    // a no-op in some browsers, so we wire a one-shot listener.
    if (pending && !restoreConsumedRef.current && pending.time > 0) {
      const seek = () => {
        try { audio.currentTime = pending.time; } catch (_e) { /* no-op */ }
      };
      if (audio.readyState >= 1) {
        seek();
      } else {
        audio.addEventListener("loadedmetadata", seek, { once: true });
      }
    }
    if (wasPlaying) {
      audio.play().catch(() => { /* autoplay blocked; user must press play */ });
    }
    if (typeof sessionStorage !== "undefined") {
      try { sessionStorage.setItem(SS_INDEX_KEY, String(index)); } catch (_e) { /* no-op */ }
    }
    // Mark the restore as consumed so future src changes start fresh.
    if (pending) {
      restoreConsumedRef.current = true;
      pendingRestoreRef.current = null;
    }
    // We intentionally omit isPlaying from deps so changing index
    // doesn't double-trigger the effect on play/pause toggles.
  }, [songs, index]);

  // ── Persist playback position + play-intent across reloads ─────
  // The version-poll auto-refresh (and any manual F5) destroys the
  // Audio element. We periodically snapshot the current position and
  // playing state to sessionStorage, plus flush on `pagehide` and
  // `beforeunload` so even an instant reload captures the latest
  // position. On boot, the pendingRestoreRef code above re-applies
  // the snapshot.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || typeof sessionStorage === "undefined") return;
    const write = () => {
      try {
        const t = audio.currentTime || 0;
        sessionStorage.setItem(SS_TIME_KEY, String(t));
        sessionStorage.setItem(SS_PLAYING_KEY, isPlaying ? "1" : "0");
        sessionStorage.setItem(SS_TIME_STAMP_KEY, String(Date.now()));
      } catch (_e) { /* no-op */ }
    };
    // Throttled to ~once every 3 seconds during playback so we keep
    // sessionStorage writes cheap. timeupdate fires ~4×/s otherwise.
    let lastWrite = 0;
    const onTimeUpdate = () => {
      const now = Date.now();
      if (now - lastWrite < 3000) return;
      lastWrite = now;
      write();
    };
    const onPageHide = () => write();
    const onBeforeUnload = () => write();
    audio.addEventListener("timeupdate", onTimeUpdate);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [isPlaying]);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || songs.length === 0) return;
    wantsToPlayRef.current = true;
    // First press → ensure src is set.
    const song = songs[index] || songs[0];
    if (song) {
      const newSrc = `${API}/playlist/${song.id}/stream`;
      if (audio.src !== newSrc) audio.src = newSrc;
    }
    try {
      await audio.play();
      setIsPlaying(true);
    } catch (_e) { /* blocked */ }
  }, [songs, index]);

  const pause = useCallback(() => {
    wantsToPlayRef.current = false;
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  const next = useCallback(() => {
    if (songs.length === 0) return;
    setIndex((cur) => (cur + 1) % songs.length);
  }, [songs]);

  const prev = useCallback(() => {
    if (songs.length === 0) return;
    setIndex((cur) => (cur - 1 + songs.length) % songs.length);
  }, [songs]);

  const jumpTo = useCallback((i) => {
    if (i < 0 || i >= songs.length) return;
    setIndex(i);
  }, [songs]);

  const changeVolume = useCallback((v) => {
    const clamped = Math.min(1, Math.max(0, v));
    setVolume(clamped);
    if (audioRef.current) audioRef.current.volume = clamped;
    if (typeof sessionStorage !== "undefined") {
      try { sessionStorage.setItem(SS_VOLUME_KEY, String(clamped)); } catch (_e) { /* no-op */ }
    }
  }, []);

  const refreshPlaylist = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/playlist`, { timeout: 8000 });
      setSongs(res.data?.items || []);
    } catch (_e) { /* keep current */ }
  }, []);

  const value = useMemo(
    () => ({
      songs,
      loaded,
      index,
      currentSong: songs[index] || null,
      isPlaying,
      volume,
      play,
      pause,
      toggle,
      next,
      prev,
      jumpTo,
      changeVolume,
      refreshPlaylist,
    }),
    [songs, loaded, index, isPlaying, volume, play, pause, toggle, next, prev, jumpTo, changeVolume, refreshPlaylist]
  );

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
}

export function useMusicPlayer() {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) {
    throw new Error("useMusicPlayer must be used inside <MusicPlayerProvider>");
  }
  return ctx;
}
